import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { toast } from '@sales-center/components'
import {
  reducer,
  initialState,
  loadRelatedCreator,
  setSavingCreator,
  unmatchServiceCreator,
} from './reducer'
import { MatchActionsProps } from '../typings'
import {
  getLocationName,
  matchCreateNew,
  useGetRelatedLocationsOnMount,
} from '@user/store/entityLocations'
import {
  APIError,
  ENTITY_TYPES,
  PestPacTimeoutExpirationStatus,
  PestPacTimeoutExpirationErrorMessage,
} from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  isAPIError,
  isAPIValidationError,
  validationErrorsToMap,
} from '@user/store/services/crud'
import { getOpportunityDetail } from '@user/store/opportunity'
import { useAppSelector } from '@user/store/hooks'
import { selectLeadDetail } from '@user/store/lead/slice'
import { selectOpportunityDetail } from '@user/store/opportunity/slice'
import { getLeadDetail } from '@user/store/lead'
import { useUpdateNoteList } from '@user/components/Notes/EntityNotes/hooks/useUpdateNoteList'
import { useData } from '@user/components/Settings/Notes/hooks/useData'
import {
  EMAIL_VALIDATION_PREFIX,
  parseEmailValidationErrorMessages,
} from '@user/store/emailValidation'

export function useActions(params: MatchActionsProps) {
  const { billToLocations, serviceLocations, entityType, entityId, onClose } =
    params
  const appDispatch = useAppDispatch()

  const billToLocation = billToLocations[0]

  const [state, dispatch] = useReducer(reducer, initialState)

  const { matches, saving, externalBillTo } = state

  const { items } = useGetRelatedLocationsOnMount(externalBillTo?.id)

  const { onUpdateNoteLIst } = useUpdateNoteList(entityId, entityType)

  const { formDefaultValues: noteSettings, noteCodes } = useData()

  useEffect(() => dispatch(loadRelatedCreator(items)), [items])
  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const onSave = useCallback(async () => {
    if (externalBillTo) {
      dispatch(setSavingCreator(true))
      try {
        const result = await appDispatch(
          matchCreateNew({
            entityType,
            data: {
              id: entityId,
              entityType,
              locations: [
                ...matches.values(),
                {
                  locationId: billToLocation.id,
                  externalEntityId: externalBillTo.id,
                  externalEntityType: 'BillTo',
                  createNewLocation: false,
                },
              ],
            },
          }),
        )
        if (matchCreateNew.fulfilled.match(result)) {
          onUpdateNoteLIst()

          toast.success(
            noteSettings.noteAutomationEnabled &&
              noteCodes.some((x) => x.id === noteSettings.defaultNoteCodeId)
              ? 'Locations successfully matched/created. Any Sales Center notes have been added to the PestPac location(s).'
              : 'Locations successfully matched/created.',
          )

          if (detail?.id == entityId) {
            if (entityType === ENTITY_TYPES.Opportunity) {
              appDispatch(getOpportunityDetail(entityId))
            } else {
              appDispatch(getLeadDetail(entityId))
            }
          }
          onClose()
        } else {
          if (result.payload) {
            if (isAPIValidationError(result.payload)) {
              const errors = validationErrorsToMap(result.payload)
              if (errors) {
                toast.error([...errors.values()].flat().join(','))
              }
            } else {
              const error = result.payload as APIError

              if (
                error &&
                error.StatusCode === PestPacTimeoutExpirationStatus
              ) {
                toast.error(PestPacTimeoutExpirationErrorMessage)
              } else if (isAPIError(result.payload)) {
                if (result.payload.Message?.includes(EMAIL_VALIDATION_PREFIX)) {
                  const splitMessages = parseEmailValidationErrorMessages(
                    result.payload.Message,
                  )

                  if (splitMessages.length) {
                    splitMessages.forEach((message) => toast.error(message))
                  } else {
                    toast.error(result.payload.Message)
                  }
                } else {
                  toast.error(result.payload.Message)
                }
              } else {
                toast.error('Failed to match/create locations')
              }
            }
          }
        }
      } finally {
        dispatch(setSavingCreator(false))
      }
    }
  }, [
    externalBillTo,
    appDispatch,
    entityType,
    entityId,
    matches,
    billToLocation.id,
    onUpdateNoteLIst,
    noteSettings.noteAutomationEnabled,
    noteSettings.defaultNoteCodeId,
    noteCodes,
    detail?.id,
    onClose,
  ])

  const hasValidationError = useMemo(() => {
    const values = [...matches.values()]

    const removedIds = values
      .filter((m) => !serviceLocations.find((l) => l.id === m.locationId))
      .map((x) => x.locationId)

    if (removedIds.length > 0) {
      dispatch(unmatchServiceCreator(removedIds))
      return true
    }

    const createIds = values
      .filter((x) => x.createNewLocation)
      .map((x) => x.locationId)

    const invalidLocations = serviceLocations.filter(
      (x) => x.isInvalid && createIds.includes(x.id),
    ).length

    return matches.size !== serviceLocations.length || invalidLocations > 0
  }, [serviceLocations, matches])

  const disableSave = saving || hasValidationError

  const label = useMemo(
    () =>
      `${
        entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity'
      }: ${getLocationName(billToLocation)}`,
    [billToLocation, entityType],
  )

  return {
    billToLocation,
    label,
    disableSave,
    onSave,
    state,
    dispatch,
  }
}
